import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Lock, LockOpen, CompareArrows } from '@mui/icons-material';
import { verifyProPin, activateProFeatures, deactivateProFeatures, PRO_FEATURES, PRO_LIMITS } from '../utils/proFeatures';

export default function ProFeaturesDialog({ open, onClose, component }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [pinAttempts, setPinAttempts] = useState(0);
  const [showFeatureComparison, setShowFeatureComparison] = useState(false);
  const isPro = localStorage.getItem('isPro') === 'true';

  useEffect(() => {
    if (open) {
      setPin('');
      setError('');
      setPinAttempts(0);
    }
  }, [open]);

  const handlePinSubmit = () => {
    if (pinAttempts >= 3) {
      setError('Too many attempts. Please try again later.');
      return;
    }

    if (verifyProPin(pin)) {
      if (isPro) {
        deactivateProFeatures();
      } else {
        activateProFeatures();
      }
      onClose();
      window.location.reload(); // Reload to apply changes
    } else {
      setPinAttempts(prev => prev + 1);
      setError(`Invalid PIN. ${3 - pinAttempts} attempts remaining.`);
    }
  };

  const renderFeatureComparison = () => {
    const features = PRO_FEATURES[component] || {};
    const limits = PRO_LIMITS[component] || {};

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Feature</TableCell>
              <TableCell align="center">Free</TableCell>
              <TableCell align="center">Pro</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(features).map(([feature, _]) => (
              <TableRow key={feature}>
                <TableCell>{feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</TableCell>
                <TableCell align="center">❌</TableCell>
                <TableCell align="center">✅</TableCell>
              </TableRow>
            ))}
            {Object.entries(limits.free).map(([limit, value]) => (
              <TableRow key={limit}>
                <TableCell>{limit.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</TableCell>
                <TableCell align="center">{value}</TableCell>
                <TableCell align="center">{limits.pro[limit] === 'unlimited' ? 'Unlimited' : limits.pro[limit]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          position: 'relative',
          zIndex: 1300
        }
      }}
    >
      <DialogTitle>
        {isPro ? 'Deactivate Pro Features' : 'Unlock Pro Features'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          
          <Typography variant="body1">
            {isPro 
              ? 'Enter your PIN to deactivate pro features.'
              : 'Enter your PIN to unlock pro features and get access to:'}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              icon={isPro ? <LockOpen /> : <Lock />}
              label="Up to 30 Cards"
              color={isPro ? "success" : "default"}
            />
            <Chip
              icon={isPro ? <LockOpen /> : <Lock />}
              label="Progress Tracking"
              color={isPro ? "success" : "default"}
            />
            <Chip
              icon={isPro ? <LockOpen /> : <Lock />}
              label="Learn Status"
              color={isPro ? "success" : "default"}
            />
            <Chip
              icon={isPro ? <LockOpen /> : <Lock />}
              label="Extended Processing Time"
              color={isPro ? "success" : "default"}
            />
          </Box>

          <TextField
            fullWidth
            label="Enter PIN"
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            error={!!error}
            helperText={error}
          />

          <Button
            variant="outlined"
            startIcon={<CompareArrows />}
            onClick={() => setShowFeatureComparison(!showFeatureComparison)}
          >
            {showFeatureComparison ? 'Hide Comparison' : 'Show Feature Comparison'}
          </Button>

          {showFeatureComparison && renderFeatureComparison()}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handlePinSubmit}
          variant="contained"
          color={isPro ? "error" : "success"}
          startIcon={isPro ? <Lock /> : <LockOpen />}
        >
          {isPro ? 'Deactivate' : 'Unlock'}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 